import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Grid,
  Header,
  Image,
  Segment,
  Message,
  Loader,
  Button,
  Divider,
  Icon,
  Dropdown,
  Modal,
  Form,
  TextArea
} from 'semantic-ui-react';
import { CustomButton } from '../../../components/common';
import arrowLeft from '../../../assets/arrow-left.svg';
// import clearIcon from '../../../assets/clear_text_fields_x.svg';
import clearIcon from '../../../assets/reset-icon-a.svg';

import {
  fetchOpportunity,
  selectSelectedOpportunity,
  selectOpportunitiesStatus,
  selectOpportunitiesError
} from '../../../slices/opportunityDataSlice';
import {
  fetchOpportunityApplications,
  updateApplication,
  selectOpportunityApplications,
  selectApplicationsStatus,
  selectApplicationsError
} from '../../../slices/applicationSlice';
import ApplicationCard from '../../../components/opportunities/ApplicationCard';
import { HeaderAndPage } from '../../../common/components';
import { format } from 'date-fns';
import docApply from '../../../assets/documents-apply.svg';
import { useMessageContext } from '../../../components/messaging/MessageProvider';
import { sendMessage } from '../../../slices/messageSlice';
import { MESSAGE_TYPES } from '../../../constants/messageConstants';

import { AuthStdFooter } from "../../../utils/auth-std-footer";
/**
 * ApplicationsList component displays all applications for a specific opportunity
 */
const ApplicationsList = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get referrer from location state, default to /opportunities/{id}
  const referrer = location.state?.from || `/opportunities/${id}`;
  const backButtonText = location.state?.backButtonText || 'Back to Opportunity';

  // Get opportunity from Redux store
  const opportunity = useSelector(selectSelectedOpportunity);
  const opportunityStatus = useSelector(selectOpportunitiesStatus);
  const opportunityError = useSelector(selectOpportunitiesError);

  // Get applications from Redux store
  const applications = useSelector(selectOpportunityApplications(id));
  const applicationsStatus = useSelector(selectApplicationsStatus);
  const applicationsError = useSelector(selectApplicationsError);

  // Get the current user
  const currentUser = useSelector(state => state.user.profile?.data);

  // State for status update
  const [statusUpdateLoading, setStatusUpdateLoading] = useState({});

  // State for filter
  const [statusFilter, setStatusFilter] = useState('all');

  // Messaging state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Get messaging context
  const { canSendMessage } = useMessageContext();

  // Fetch opportunity and applications on component mount
  useEffect(() => {
    if (id) {
      dispatch(fetchOpportunity(id));
      dispatch(fetchOpportunityApplications(id));
    }
  }, [dispatch, id]);

  // Filter applications by status
  const filteredApplications = statusFilter === 'all'
    ? applications
    : applications.filter(app => app.status === statusFilter);

  // Handle view application button click
  const handleViewApplication = (applicationId) => {
    navigate(`/applications/${applicationId}`);
  };

  // Helper: send an APPLICATION_STATUS_UPDATE message after status change
  const sendStatusUpdateMessage = (application, newStatus) => {
    if (!currentUser?.userId || !application?.actor?.userId) return;

    const actorName = `${application.actor?.firstName || ''} ${application.actor?.lastName || ''}`.trim();
    const statusLabel = newStatus === 'Accepted' ? 'accepted' : 'rejected';

    const content = newStatus === 'Accepted'
      ? `Hi ${actorName}, your application for "${opportunity?.title}" has been accepted! We'll be in touch with next steps.`
      : `Hi ${actorName}, thank you for your interest in "${opportunity?.title}". Unfortunately, we've decided to go with another candidate for this opportunity.`;

    dispatch(sendMessage({
      senderId: currentUser.userId,
      recipientId: application.actor.userId,
      content,
      messageType: MESSAGE_TYPES.APPLICATION_STATUS_UPDATE,
      metadata: {
        applicationId: application.uuid,
        opportunityId: opportunity?.uuid,
        opportunityTitle: opportunity?.title,
        newStatus,
        timestamp: new Date().toISOString()
      }
    })).catch(err => console.error(`Failed to send ${statusLabel} notification:`, err));
  };

  // Handle accept application button click
  const handleAcceptApplication = (applicationId) => {
    setStatusUpdateLoading(prev => ({ ...prev, [applicationId]: true }));

    dispatch(updateApplication({
      applicationId,
      applicationData: { status: 'Accepted' }
    }))
      .unwrap()
      .then(() => {
        setStatusUpdateLoading(prev => ({ ...prev, [applicationId]: false }));
        // Send APPLICATION_STATUS_UPDATE message to the voice actor
        const app = applications.find(a => a.uuid === applicationId);
        if (app) sendStatusUpdateMessage(app, 'Accepted');
      })
      .catch(error => {
        console.error('Failed to accept application:', error);
        setStatusUpdateLoading(prev => ({ ...prev, [applicationId]: false }));
      });
  };

  // Handle reject application button click
  const handleRejectApplication = (applicationId) => {
    setStatusUpdateLoading(prev => ({ ...prev, [applicationId]: true }));

    dispatch(updateApplication({
      applicationId,
      applicationData: { status: 'Rejected' }
    }))
      .unwrap()
      .then(() => {
        setStatusUpdateLoading(prev => ({ ...prev, [applicationId]: false }));
        // Send APPLICATION_STATUS_UPDATE message to the voice actor
        const app = applications.find(a => a.uuid === applicationId);
        if (app) sendStatusUpdateMessage(app, 'Rejected');
      })
      .catch(error => {
        console.error('Failed to reject application:', error);
        setStatusUpdateLoading(prev => ({ ...prev, [applicationId]: false }));
      });
  };

  // Handle status filter change
  const handleStatusFilterChange = (e, { value }) => {
    setStatusFilter(value);
  };

  // Handle message application
  const handleMessageApplication = (application) => {
    if (!canSendMessage(MESSAGE_TYPES.APPLICATION_RESPONSE)) {
      alert('You do not have permission to send messages.');
      return;
    }

    setSelectedApplication(application);

    // Generate initial message content based on application status
    let initialMessage = '';
    const actorName = `${application.actor?.firstName || ''} ${application.actor?.lastName || ''}`.trim();

    if (application.status === 'Pending') {
      initialMessage = `Hi ${actorName},

Thank you for your application for the "${opportunity.title}" opportunity. I've reviewed your submission and would like to discuss a few things with you.

I'm particularly interested in:
- Your experience with similar projects
- Your availability for the estimated timeline
- Any questions you might have about the project

Please let me know when would be a good time to discuss this further.

Best regards,
${currentUser?.name || 'Opportunity Creator'}`;
    } else if (application.status === 'Accepted') {
      initialMessage = `Hi ${actorName},

Congratulations! I'm pleased to inform you that your application for "${opportunity.title}" has been accepted.

I'd like to discuss the next steps including:
- Project timeline and milestones
- Recording schedule and deadlines
- Contract terms and compensation details
- Any technical requirements

Please let me know your availability so we can move forward with the project.

Best regards,
${currentUser?.name || 'Opportunity Creator'}`;
    } else if (application.status === 'Rejected') {
      initialMessage = `Hi ${actorName},

Thank you for your interest in the "${opportunity.title}" opportunity. While we've decided to go with another candidate for this particular project, I was impressed with your application.

I'd like to keep you in mind for future opportunities that might be a better fit. Would you be interested in discussing other potential projects?

Best regards,
${currentUser?.name || 'Opportunity Creator'}`;
    }

    setMessageContent(initialMessage);
    setShowMessageModal(true);
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (!messageContent.trim() || !selectedApplication) {
      alert('Please enter a message.');
      return;
    }

    setIsSendingMessage(true);
    try {
      const messageData = {
        senderId: currentUser.userId,
        recipientId: selectedApplication.actor.userId,
        content: messageContent,
        messageType: MESSAGE_TYPES.APPLICATION_RESPONSE,
        metadata: {
          applicationId: selectedApplication.uuid,
          opportunityId: opportunity.uuid,
          opportunityTitle: opportunity.title,
          applicationStatus: selectedApplication.status,
          timestamp: new Date().toISOString()
        }
      };

      await dispatch(sendMessage(messageData));

      setShowMessageModal(false);
      setSelectedApplication(null);
      setMessageContent('');

      // Show success message
      alert('Message sent successfully!');

    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSendingMessage(false);
    }
  };

  // Handle close message modal
  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    setSelectedApplication(null);
    setMessageContent('');
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  // Check if the current user is the creator of the opportunity
  const isCreator = opportunity?.creator?.userId === currentUser?.userId;

  // Render loading state
  if ((opportunityStatus === 'loading' && !opportunity) || (applicationsStatus === 'loading' && applications.length === 0)) {
    return (
      <HeaderAndPage
        headerImage={docApply}
        headerText="Applications"
        subHeaderText="Loading applications..."
        doCenter={true}

        style={{ marginBottom: "70px" }}>
        <Segment basic textAlign="center" style={{ minHeight: '300px' }}>
          <Loader active size="large">Loading Applications</Loader>
        </Segment>
      </HeaderAndPage>
    );
  }

  // Render error state
  if (opportunityStatus === 'failed' || applicationsStatus === 'failed') {
    return (
      <HeaderAndPage
        headerImage={docApply}
        headerText="Applications"
        subHeaderText="Error loading applications"
        doCenter={true}

      >
        <Message negative>
          <Message.Header>Error Loading Applications</Message.Header>
          <p>{opportunityError || applicationsError}</p>
          <CustomButton
            onClick={() => navigate(-1)}
            color="grey"
            icon={arrowLeft}
            size="tiny"

            style={{ marginTop: '10px' }}
          >
            {backButtonText}
          </CustomButton>
        </Message>
      </HeaderAndPage>
    );
  }

  // Render not found state
  if (!opportunity) {
    return (
      <HeaderAndPage
        headerImage={docApply}
        headerText="Applications"
        subHeaderText="Opportunity not found"
        doCenter={true}

      >
        <Message warning>
          <Message.Header>Opportunity Not Found</Message.Header>
          <p>The opportunity you are looking for does not exist or has been removed.</p>
          <CustomButton
            onClick={() => navigate(-1)}
            color="grey"
            icon={arrowLeft}
            size="tiny"

            style={{ marginTop: '10px' }}
          >
            {backButtonText}
          </CustomButton>
        </Message>
      </HeaderAndPage>
    );
  }

  // Render unauthorized state
  if (!isCreator) {
    return (
      <HeaderAndPage
        headerImage={docApply}
        headerText="Applications"
        subHeaderText="Unauthorized"
        doCenter={true}

      >
        <Message negative>
          <Message.Header>Unauthorized</Message.Header>
          <p>You do not have permission to view applications for this opportunity.</p>
          <CustomButton
            onClick={() => navigate(-1)}
            color="grey"
            icon={arrowLeft}
            size="tiny"

            style={{ marginTop: '10px' }}
          >
            {backButtonText}
          </CustomButton>
        </Message>
      </HeaderAndPage>
    );
  }

  // Render empty state
  if (applications.length === 0) {
    return (
      <HeaderAndPage
        headerImage={docApply}
        headerText={`Applications for:`}
        subHeaderText={`${opportunity.title}`}
        // subHeaderText={`${opportunity.title} - No applications yet`}

        doCenter={true}

      >
        <Message info>
          <Message.Header>No Applications Yet</Message.Header>
          <p>There are no applications for this opportunity yet. Check back later.</p>
          <CustomButton
            onClick={() => navigate(-1)}
            color="grey"
            icon={arrowLeft}
            size="tiny"

            style={{ marginTop: '10px' }}
          >
            {backButtonText}
          </CustomButton>
        </Message>
      </HeaderAndPage>
    );
  }

  // Render applications
  return (
    <HeaderAndPage
      headerImage={docApply}
      headerText={`Applications - ${opportunity.characterName || opportunity.character?.name || 'Character'}`}
      subHeaderText={opportunity.title}
      doCenter={true}
    >
      <Segment>
        <Grid verticalAlign="middle">
          <Grid.Row>
            <Grid.Column width={8}>
              <Header as="h3" style={{ marginBottom: '4px' }}>Applications</Header>
              <p style={{ margin: 0, color: '#666' }}>Showing {filteredApplications.length} of {applications.length} applications</p>
            </Grid.Column>
            <Grid.Column width={8} textAlign="right">
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
                <Dropdown
                  text="Filter by Status"
                  icon="filter"
                  floating
                  labeled
                  button
                  className="icon"
                  disabled={applications.length <= 1}
                  style={{ height: '36px', display: 'flex', alignItems: 'center' }}
                >
                  <Dropdown.Menu>
                    <Dropdown.Header content="Filter by Status" />
                    <Dropdown.Item
                      text="All"
                      active={statusFilter === 'all'}
                      onClick={(e, data) => handleStatusFilterChange(e, { value: 'all' })}
                    />
                    <Dropdown.Item
                      text="Pending"
                      active={statusFilter === 'Pending'}
                      onClick={(e, data) => handleStatusFilterChange(e, { value: 'Pending' })}
                    />
                    <Dropdown.Item
                      text="Accepted"
                      active={statusFilter === 'Accepted'}
                      onClick={(e, data) => handleStatusFilterChange(e, { value: 'Accepted' })}
                    />
                    <Dropdown.Item
                      text="Rejected"
                      active={statusFilter === 'Rejected'}
                      onClick={(e, data) => handleStatusFilterChange(e, { value: 'Rejected' })}
                    />
                  </Dropdown.Menu>
                </Dropdown>
                <CustomButton
                  onClick={() => navigate(-1)}
                  color="grey"
                  icon={arrowLeft}
                  size="tiny"
                >
                  {backButtonText}
                </CustomButton>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Divider />

        <Grid stackable>
          {filteredApplications.map(application => (
            <Grid.Column width={16} key={application.uuid} style={{ marginBottom: '20px' }}>
              <ApplicationCard
                application={application}
                onViewDetails={handleViewApplication}
                onAccept={handleAcceptApplication}
                onReject={handleRejectApplication}
                onMessage={handleMessageApplication}
                isCreator={true}
                viewMode="detailed"
                userId={currentUser?.userId}
              />
            </Grid.Column>
          ))}
        </Grid>
      </Segment>

      {/* Message Modal */}
      <Modal
        open={showMessageModal}
        onClose={handleCloseMessageModal}
        size="small"
      >
        <Modal.Header>
          <Icon name="mail" />
          Message Voice Actor
        </Modal.Header>
        <Modal.Content>
          {selectedApplication && (
            <Message info>
              <Message.Header>
                Sending message to: {selectedApplication.actor?.firstName} {selectedApplication.actor?.lastName}
              </Message.Header>
              <p>
                Regarding application for: <strong>{opportunity.title}</strong>
                <br />
                Application Status: <strong>{selectedApplication.status}</strong>
              </p>
            </Message>
          )}

          <Form>
            <Form.Field>
              <label>Your Message</label>
              <TextArea
                placeholder="Enter your message here..."
                value={messageContent}
                onChange={(e, { value }) => setMessageContent(value)}
                rows={12}
                style={{ minHeight: '250px' }}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <CustomButton
            onClick={handleCloseMessageModal}
            icon={clearIcon}
            color="grey"
            size="tiny"
          >
            Cancel
          </CustomButton>
          <CustomButton
            color="blue"
            onClick={handleSendMessage}
            loading={isSendingMessage}
            disabled={!messageContent.trim()}
            size="tiny"
            style={{ marginLeft: '10px' }}
          >
            Send Message
          </CustomButton>
        </Modal.Actions>
      </Modal>
      {AuthStdFooter()}
    </HeaderAndPage>
  );
};

export default ApplicationsList;
